/* tslint:disable:prefer-for-of component-selector */
import { Component } from '@angular/core';
import { map } from 'rxjs/operators';
import { NinjaCumulativeData, NinjaData } from '../../models/ninja-data.model';
import { Dataset } from '../../models/dataset.model';
import { forkJoin, Observable, zip } from 'rxjs';
import { DataService } from '../data.service';
import { UtilityService } from '../utility.service';

@Component({
  selector: 'chart-view',
  templateUrl: './chart-view.component.html',
  styleUrls: ['./chart-view.component.scss']
})
export class ChartViewComponent {

  constructor(
    private dataService: DataService,
    private utilityService: UtilityService
  ) {
  }

  isDelta = false;
  limit = 0.6;

  continentData$: Observable<NinjaData[]> = this.dataService.getContinentsData();
  sortedCountries$: Observable<NinjaData[]> = this.dataService.getSortedCountries();
  cumulativeData$: Observable<NinjaCumulativeData> = this.dataService.getCumulativeData();
  countries$: Observable<string[]> = this.sortedCountries$
    .pipe(
      map(countries => countries.map(country => country.country))
    );

  // ——————————————————————————————————
  // Data for mortality rate bar chart
  // ———————————————————————————————————
  mortalityRate$: Observable<number[]> = this.sortedCountries$
    .pipe(
      map(countries => {
        const data = [];
        for (const country of countries) {
          data.push(country.deaths / country.cases * 100).toFixed(4);
        }
        return data;
      })
    );

  sortedMortalityRate$: Observable<Dataset[]> = zip(this.mortalityRate$, this.countries$)
    .pipe(
      map(([data, countries]) => {
        return this.utilityService.sort(data, countries);
      }));

  deathsPerMillion$: Observable<number[]> = this.sortedCountries$
    .pipe(
      map(countries => {
        const data = [];
        for (const country of countries) {
          data.push(country.deaths / country.population * 1000000).toFixed(4);
        }
        return data;
      })
    );
  sortedDeathsPerMillion$: Observable<Dataset[]> = zip(this.deathsPerMillion$, this.countries$)
    .pipe(
      map(([data, countries]) => {
        return this.utilityService.sort(data, countries);
      }));

  casesPerMillion$: Observable<number[]> = this.sortedCountries$
    .pipe(
      map(countries => {
        const data = [];
        for (const country of countries) {
          data.push(country.cases / country.population * 1000000).toFixed(4);
        }
        return data;
      })
    );
  sortedCasesPerMillion$: Observable<Dataset[]> = zip(this.casesPerMillion$, this.countries$)
    .pipe(
      map(([data, countries]) => {
        return this.utilityService.sort(data, countries);
      }));

  worldVaccines$ = this.dataService.getWorldVaccinesCoverage();
  worldVaccinesCoverage$: Observable<number[]> = this.worldVaccines$
    .pipe(
      map(data => {
        return Object.values(data);
      })
    );
  worldVaccinesCoverageDelta$: Observable<number[]> = this.utilityService.calculateDelta(this.worldVaccinesCoverage$);

  worldVaccinesCoverageDates$: Observable<string[]> = this.worldVaccines$
    .pipe(
      map(data => {
        return Object.keys(data);
      })
    );

  diseaseShData$ = this.dataService.getDiseaseHistoricalData();
  historicalDiseaseShDates$: Observable<string[]> = this.diseaseShData$
    .pipe(
      map((data: any) => {
        return Object.keys(data.cases);
      })
    );
  historicalDiseaseDeaths$: Observable<number[]> = this.diseaseShData$
    .pipe(
      map((data: any) => {
        return Object.values(data.deaths);
      })
    );
  historicalDiseaseDeltaDeaths$: Observable<number[]> = this.utilityService.calculateDelta(this.historicalDiseaseDeaths$);

  historicalDiseaseCases$: Observable<number[]> = this.diseaseShData$
    .pipe(
      map((data: any) => {
        return Object.values(data.cases);
      })
    );
  historicalDiseaseDeltaCases$: Observable<number[]> = this.utilityService.calculateDelta(this.historicalDiseaseCases$);

  historicalDiseaseRecovered$: Observable<number[]> = this.diseaseShData$
    .pipe(
      map((data: any) => {
        return Object.values(data.recovered);
      })
    );

  historicalDiseaseDeltaRecovered$: Observable<number[]> = this.utilityService.calculateDelta(this.historicalDiseaseRecovered$);

  historicalMortalityRate$: Observable<number[]> = zip(this.historicalDiseaseDeaths$, this.historicalDiseaseCases$)
    .pipe(
      map((data) => {
        return this.utilityService.divideArrays(data[0], data[1]);
      })
    );


  forkedData$ = forkJoin({
    continentData: this.continentData$,
    cumulativeData: this.cumulativeData$,
    worldVaccinesCoverage: this.worldVaccinesCoverage$,
    worldVaccinesCoverageDelta: this.worldVaccinesCoverageDelta$,
    worldVaccinesCoverageDates: this.worldVaccinesCoverageDates$,
    historicalDiseaseShDates: this.historicalDiseaseShDates$,
    historicalDiseaseDeaths: this.historicalDiseaseDeaths$,
    historicalDiseaseDeltaDeaths: this.historicalDiseaseDeltaDeaths$,
    historicalDiseaseCases: this.historicalDiseaseCases$,
    historicalDiseaseDeltaCases: this.historicalDiseaseDeltaCases$,
    historicalDiseaseRecovered: this.historicalDiseaseRecovered$,
    historicalDiseaseDeltaRecovered: this.historicalDiseaseDeltaRecovered$,
    historicalMortalityRate: this.historicalMortalityRate$,
    sortedMortalityRate: this.sortedMortalityRate$,
    sortedDeathsPerMillion: this.sortedDeathsPerMillion$,
    sortedCasesPerMillion: this.sortedCasesPerMillion$,
  });

  getLimit(limit: number): void {
    this.limit = limit;
  }

}
